import * as AccountDomain from "../../domain/sfp/account-domain"
import * as Model from "../../model"
import * as PpiDomain from "./ppi-domain"
import * as LoanDomain from "./loan-domain"

import {
    Account,
    Observation,
    LocalAuthorityChapterAccount
} from "../../model"

const INVESTMENTS_CHAPTER = ["20", "204", "22", "21", "23"]

/** Numéro du chapitre des subventions */
export const SUBVENTION_CHAPTER_NUMBER = "13"
/** Numéro des comptes d'emprunts */
export const LOAN_ACCOUNTS_NUMBER = "164"

export const DATA_INVESTMENTS_ROW_INDEX = 0
export const DATA_SUBVENTIONS_ROW_INDEX = 1
export const DATA_SUBVENTIONS_DEMANDEES_INDEX = 2
export let DATA_LOANS_ROW_INDEX = 2
export let DATA_AUTOFINANCING_ROW_INDEX = 3

export function createInvestmentData(
    observations: Observation[],
    lacas: LocalAuthorityChapterAccount[],
    years: number[],
    ppi: Model.Ppi,
    loans: any
): InvestmentData {
    const currentYear = new Date().getFullYear();
    let hasProspectiveYear: boolean = years.some(y => y >= currentYear);

    if (hasProspectiveYear) {
        DATA_LOANS_ROW_INDEX = 3;
        DATA_AUTOFINANCING_ROW_INDEX = 4;
    } else {
        DATA_LOANS_ROW_INDEX = 2;
        DATA_AUTOFINANCING_ROW_INDEX = 3;
    }

    return years.reduce<InvestmentData>((data, year, yearIndex) => {

        if (year >= currentYear) {

            const totalInvest = PpiDomain.getTotalInvests(year, ppi);
            data[DATA_INVESTMENTS_ROW_INDEX].values[yearIndex] += totalInvest
            data[DATA_INVESTMENTS_ROW_INDEX].total += totalInvest
            data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] += totalInvest
            data[DATA_AUTOFINANCING_ROW_INDEX].total += totalInvest

            const totalSubventionsOctroyees = PpiDomain.getGrantsAmount(true, year, ppi);
            data[DATA_SUBVENTIONS_ROW_INDEX].values[yearIndex] += totalSubventionsOctroyees
            data[DATA_SUBVENTIONS_ROW_INDEX].total += totalSubventionsOctroyees
            data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] -= totalSubventionsOctroyees
            data[DATA_AUTOFINANCING_ROW_INDEX].total -= totalSubventionsOctroyees

            const totalSubventionsDemandees = PpiDomain.getGrantsAmount(false, year, ppi);
            data[DATA_SUBVENTIONS_DEMANDEES_INDEX].values[yearIndex] += totalSubventionsDemandees
            data[DATA_SUBVENTIONS_DEMANDEES_INDEX].total += totalSubventionsDemandees
            data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] -= totalSubventionsDemandees
            data[DATA_AUTOFINANCING_ROW_INDEX].total -= totalSubventionsDemandees

            const loanYear = loans.find((l: any) => l.year === year)
            const totalEmprunts = loanYear ? loanYear.total : 0;
            data[DATA_LOANS_ROW_INDEX].values[yearIndex] += totalEmprunts
            data[DATA_LOANS_ROW_INDEX].total += totalEmprunts
            data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] -= totalEmprunts
            data[DATA_AUTOFINANCING_ROW_INDEX].total -= totalEmprunts            
        }

        observations.forEach(o => {
            if (o.year !== year) return

            if (accountIsSubvention(o.account, lacas)) {
                data[DATA_SUBVENTIONS_ROW_INDEX].values[yearIndex] += o.amount
                data[DATA_SUBVENTIONS_ROW_INDEX].total += o.amount

                data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] -= o.amount
                data[DATA_AUTOFINANCING_ROW_INDEX].total -= o.amount
                return
            }

            if (accountIsLoan(o)) {
                if (o.amount > 0) {
                    data[DATA_LOANS_ROW_INDEX].values[yearIndex] += o.amount
                    data[DATA_LOANS_ROW_INDEX].total += o.amount
                }
                data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] -= o.amount
                data[DATA_AUTOFINANCING_ROW_INDEX].total -= o.amount
                return
            }

            if (accountIsInvestment(o, lacas)) {
                data[DATA_INVESTMENTS_ROW_INDEX].values[yearIndex] += o.amount
                data[DATA_INVESTMENTS_ROW_INDEX].total += o.amount

                data[DATA_AUTOFINANCING_ROW_INDEX].values[yearIndex] += o.amount
                data[DATA_AUTOFINANCING_ROW_INDEX].total += o.amount
            }
        })

        return data
    }, getArrayForInvest(hasProspectiveYear, years)
    )
}

function getArrayForInvest(hasProspectiveYear: boolean, years: number[]) {
    const ROW_LABELS = {
        investments: "Investissements réalisés",
        grantsGranted: "Subventions octroyées",
        grantsRequested: "Subventions demandées",
        grantsReceived: "Subventions reçues",
        loans: "Emprunts",
        autofinancing: "Autofinancement"
        };
        const makeRow = (label: string) => ({
            label,
            values: years.map(() => 0),
            total: 0
        });

        const labels = hasProspectiveYear
            ? [
                ROW_LABELS.investments,
                ROW_LABELS.grantsGranted,
                ROW_LABELS.grantsRequested,
                ROW_LABELS.loans,
                ROW_LABELS.autofinancing
            ]
            : [
                ROW_LABELS.investments,
                ROW_LABELS.grantsReceived,
                ROW_LABELS.loans,
                ROW_LABELS.autofinancing
            ];

        return labels.map(makeRow);
}

/**
 * Vérifie si le compte passé en paramètre est un investissement.
 * Un compte est un investissement s'il n'est pas en recette et si le chapitre
 * auquel il est rattaché est de type dépense d'équipement.
 * @param account
 * @param lacas
 */
export function accountIsInvestment(
    observation: Observation,
    lacas: LocalAuthorityChapterAccount[]
): boolean {
    const account = observation.account
    const chapter = AccountDomain.getAttachedChapter(account, lacas)
    // let expense = true
    let investmentsChap = chapter && INVESTMENTS_CHAPTER.some(n => chapter.number.startsWith(n))

    return !observation.receipts && account.expense && investmentsChap ? true : false

    // if (!ChapterDomain.isEquipment(chapter, undefined, expense))
    // 	return false

    // return !account.receipts
}

/**
 * Vérifie si le compte passé en paramètre est un emprunt.
 * Un compte est un emprunt s'il est en recette et si son numéro commence par
 * {@link LOAN_ACCOUNTS_NUMBER}.
 * @param observation
 */
export function accountIsLoan(observation: Observation): boolean {
    return observation.receipts && observation.account.number.startsWith(LOAN_ACCOUNTS_NUMBER)
}

/**
 * Vérifie si le compte passé en paramètre est une subvention.
 * Un compte est une subvention s'il est en recette et s'il est rattaché au
 * chapitre {@link SUBVENTION_CHAPTER_NUMBER}.
 * @param account
 * @param lacas
 */
export function accountIsSubvention(
    account: Account,
    lacas: LocalAuthorityChapterAccount[]
): boolean {
    const chapter = AccountDomain.getAttachedChapter(account, lacas)
    return account.receipts && chapter && "13" === chapter.number
}

export type InvestmentData = InvestmentDataRow[]

export interface InvestmentDataRow {
    label: string
    values: number[]
    total: number
}

/**
 * Calcule l'autofinancement.
 * Autofinancement = investissements - subventions - emprunts
 * @param investments
 * @param subventions
 * @param loans
 */
export function CalculateAutofinancing(
    investments: number,
    subventions: number,
    loans: number
): number {
    return investments - subventions - loans
}


// WEBPACK FOOTER //
// ./src/domain/sfp/investment-domain.ts